import { useMemo, useRef, useState } from 'react';
import { Badge, Button, Form, Overlay, Popover, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import type React from 'react';

import { BotaoAcaoLinha } from '../../components/BotaoAcaoLinha';
import { garantirOpcaoTipoRecurso, rotuloTipoRecurso } from '../../utils/tipo-recurso-options.utils';
import type { NaturezaAcaoOption, ProcessoItem, RelatorioCivelFormData } from '../relatorio-civel-page.models';
import type { QuantidadeLinhasState } from '../steps/relatorio-civel-step-props';

export type ProcessoSection =
  | 'contestacao'
  | 'reconvencao'
  | 'replica'
  | 'manifestacao'
  | 'razoesFinais'
  | 'recursos'
  | 'outrosProcedimentos'
  | 'outrasManifestacoes'
  | 'ciencia';

type UseProcessosSectionsParams = {
  formData: RelatorioCivelFormData;
  setFormData: React.Dispatch<React.SetStateAction<RelatorioCivelFormData>>;
  quantidadeLinhas: QuantidadeLinhasState;
  apenasNumeros: (value: string) => string;
  isViewMode: boolean;
  naturezaAcaoOptions?: NaturezaAcaoOption[];
  tipoRecursoOptions?: NaturezaAcaoOption[];
  RELATORIO_CORES?: Record<string, string>;
};

export function useProcessosSections({
  setFormData,
  quantidadeLinhas,
  apenasNumeros,
  isViewMode,
  naturezaAcaoOptions = [{ value: '', label: 'Selecione...' }],
  tipoRecursoOptions = [{ value: '', label: 'Selecione...' }],
  RELATORIO_CORES,
}: UseProcessosSectionsParams): {
  adicionarProcesso: (
    section: ProcessoSection,
    numeroProcesso: string,
    setNovoProcesso: (value: string) => void,
    extras?: { tipoRecurso?: string; setTipoRecurso?: (value: string) => void },
  ) => void;
  adicionarMultiplasLinhas: (section: ProcessoSection) => void;
  renderTabelaProcessos: (section: ProcessoSection, processos: ProcessoItem[]) => React.ReactNode;
} {
  const [editandoProcesso, setEditandoProcesso] = useState<{ section: ProcessoSection; id: string } | null>(null);
  const [valorEdicao, setValorEdicao] = useState('');
  const [valorEdicaoTipoRecurso, setValorEdicaoTipoRecurso] = useState('');

  const [confirmandoExclusao, setConfirmandoExclusao] = useState<{ section: ProcessoSection; id: string } | null>(null);
  const excluirButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const adicionarProcesso = (
    section: ProcessoSection,
    numeroProcesso: string,
    setNovoProcesso: (value: string) => void,
    extras?: { tipoRecurso?: string; setTipoRecurso?: (value: string) => void },
  ) => {
    if (isViewMode) return;

    if (!numeroProcesso.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    const novoItem: ProcessoItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: numeroProcesso.trim(),
      dataAdicao: new Date(),
      ...(section === 'recursos' ? { tipoRecurso: (extras?.tipoRecurso ?? '').trim() } : {}),
    };

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] as { processos: ProcessoItem[] }),
        processos: [...(prev[section] as { processos: ProcessoItem[] }).processos, novoItem],
      },
    }));

    setNovoProcesso('');
    extras?.setTipoRecurso?.('');
    toast.success('Processo adicionado com sucesso!');
  };

  const removerProcesso = (section: ProcessoSection, id: string) => {
    if (isViewMode) return;

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] as { processos: ProcessoItem[] }),
        processos: (prev[section] as { processos: ProcessoItem[] }).processos.filter((p) => p.id !== id),
      },
    }));
    toast.info('Processo removido.');
  };

  const iniciarEdicao = (section: ProcessoSection, processo: ProcessoItem) => {
    if (isViewMode) return;
    setEditandoProcesso({ section, id: processo.id });
    setValorEdicao(processo.numeroProcesso);
    setValorEdicaoTipoRecurso(processo.tipoRecurso ?? '');
  };

  const cancelarEdicao = () => {
    setEditandoProcesso(null);
    setValorEdicao('');
    setValorEdicaoTipoRecurso('');
  };

  const salvarEdicao = (section: ProcessoSection, id: string) => {
    if (isViewMode) return;

    if (!valorEdicao.trim()) {
      toast.warning('O número do processo não pode estar vazio.');
      return;
    }

    setFormData((prev) => {
      const secaoAnterior = prev[section] as { processos: ProcessoItem[] };
      return {
        ...prev,
        [section]: {
          ...secaoAnterior,
          processos: secaoAnterior.processos.map((p) =>
            p.id === id
              ? {
                  ...p,
                  numeroProcesso: valorEdicao.trim(),
                  ...(section === 'recursos' ? { tipoRecurso: valorEdicaoTipoRecurso.trim() } : {}),
                }
              : p,
          ),
        },
      };
    });

    setEditandoProcesso(null);
    setValorEdicao('');
    setValorEdicaoTipoRecurso('');
    toast.success('Processo atualizado com sucesso!');
  };

  const adicionarMultiplasLinhas = (section: ProcessoSection) => {
    if (isViewMode) return;

    const quantidade = quantidadeLinhas[section];
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosProcessos: ProcessoItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosProcessos.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        dataAdicao: new Date(),
        ...(section === 'recursos' ? { tipoRecurso: '' } : {}),
      });
    }

    setFormData((prev) => {
      const secaoAnterior = prev[section] as { processos: ProcessoItem[] };
      return {
        ...prev,
        [section]: {
          ...secaoAnterior,
          processos: [...secaoAnterior.processos, ...novosProcessos],
        },
      };
    });

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  const confirmarExclusao = (section: ProcessoSection, id: string) => {
    removerProcesso(section, id);
    setConfirmandoExclusao(null);
  };

  const renderTabelaProcessos = useMemo(() => {
    return (section: ProcessoSection, processos: ProcessoItem[]) => {
      const isRecursos = section === 'recursos';

      if (processos.length === 0) {
        return (
          <div className="text-center text-muted py-4 border rounded">
            <i className="bi bi-inbox fs-1"></i>
            <p className="mb-0 mt-2">Nenhum processo adicionado ainda.</p>
            <small>Adicione os números dos processos acima.</small>
          </div>
        );
      }

      return (
        <Table striped bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th style={{ width: '50px' }}>#</th>
              <th>Número do Processo</th>
              {isRecursos && <th>Tipo Recurso</th>}
              {!isViewMode && <th style={{ width: '200px' }}>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {processos.map((processo, index) => {
              const estaEditando = editandoProcesso?.section === section && editandoProcesso?.id === processo.id;
              const estaConfirmando =
                confirmandoExclusao?.section === section && confirmandoExclusao?.id === processo.id;
              const refKey = `${section}-${processo.id}`;
              const opcoesTipoRecursoLinha = isRecursos
                ? garantirOpcaoTipoRecurso(
                    tipoRecursoOptions,
                    estaEditando ? valorEdicaoTipoRecurso : processo.tipoRecurso,
                    naturezaAcaoOptions,
                  )
                : tipoRecursoOptions;

              return (
                <tr key={processo.id}>
                  <td>{index + 1}</td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={valorEdicao}
                        onChange={(e) => setValorEdicao(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            salvarEdicao(section, processo.id);
                          }
                          if (e.key === 'Escape') {
                            cancelarEdicao();
                          }
                        }}
                        style={{ borderRadius: '8px' }}
                        autoFocus
                      />
                    ) : processo.numeroProcesso ? (
                      <code>{processo.numeroProcesso}</code>
                    ) : (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Digite o número do processo"
                        style={{ borderRadius: '8px' }}
                        value={processo.numeroProcesso || ''}
                        onChange={(e) => {
                          const valorApenasNumeros = apenasNumeros(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            [section]: {
                              ...prev[section],
                              processos: prev[section].processos.map((p) =>
                                p.id === processo.id ? { ...p, numeroProcesso: valorApenasNumeros } : p,
                              ),
                            },
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (
                            e.key.length === 1 &&
                            !/[0-9]/.test(e.key) &&
                            e.key !== 'Enter' &&
                            e.key !== 'Backspace' &&
                            e.key !== 'Delete' &&
                            e.key !== 'Tab'
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  </td>
                  {isRecursos && (
                    <td>
                      {estaEditando ? (
                        <Form.Select
                          size="sm"
                          value={valorEdicaoTipoRecurso}
                          onChange={(e) => setValorEdicaoTipoRecurso(e.target.value)}
                          style={{ borderRadius: '8px' }}
                        >
                          {opcoesTipoRecursoLinha.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      ) : processo.tipoRecurso ? (
                        <Badge style={{ backgroundColor: RELATORIO_CORES?.PRIMARIA }}>
                          {rotuloTipoRecurso(processo.tipoRecurso, tipoRecursoOptions, naturezaAcaoOptions)}
                        </Badge>
                      ) : isViewMode ? (
                        '-'
                      ) : (
                        <Form.Select
                          size="sm"
                          style={{ borderRadius: '8px' }}
                          value={processo.tipoRecurso || ''}
                          onChange={(e) => {
                            const tipoRecurso = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              [section]: {
                                ...prev[section],
                                processos: prev[section].processos.map((p) =>
                                  p.id === processo.id ? { ...p, tipoRecurso } : p,
                                ),
                              },
                            }));
                          }}
                        >
                          {opcoesTipoRecursoLinha.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </td>
                  )}
                  {!isViewMode && (
                    <td>
                      {estaEditando ? (
                        <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                          <BotaoAcaoLinha tipo="salvar" onClick={() => salvarEdicao(section, processo.id)} />
                          <BotaoAcaoLinha tipo="cancelar" onClick={cancelarEdicao} />
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                          <BotaoAcaoLinha tipo="editar" onClick={() => iniciarEdicao(section, processo)} />
                          <BotaoAcaoLinha
                            ref={(el) => {
                              excluirButtonRefs.current[refKey] = el;
                            }}
                            tipo="excluir"
                            onClick={() => setConfirmandoExclusao({ section, id: processo.id })}
                          />
                          <Overlay
                            show={estaConfirmando}
                            target={excluirButtonRefs.current[refKey]}
                            placement="top"
                            containerPadding={20}
                          >
                            <Popover id={`popover-delete-${refKey}`}>
                              <Popover.Header as="h3" className="text-center">
                                Deseja mesmo excluir?
                              </Popover.Header>
                              <Popover.Body className="d-flex gap-2 justify-content-center">
                                <Button variant="danger" size="sm" onClick={() => confirmarExclusao(section, processo.id)}>
                                  Sim
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => setConfirmandoExclusao(null)}>
                                  Não
                                </Button>
                              </Popover.Body>
                            </Popover>
                          </Overlay>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      );
    };
  }, [
    apenasNumeros,
    confirmandoExclusao,
    editandoProcesso,
    isViewMode,
    naturezaAcaoOptions,
    tipoRecursoOptions,
    RELATORIO_CORES,
    setFormData,
    valorEdicao,
    valorEdicaoTipoRecurso,
  ]);

  return {
    adicionarProcesso,
    adicionarMultiplasLinhas,
    renderTabelaProcessos,
  };
}

